import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
} from "@mui/material";

import { login } from "@/api/auth";
const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const successMessage = searchParams.get("message");
  const reason = searchParams.get("reason");
  const inactivityMessage =
    reason === "inactivity"
      ? "Sie wurden aufgrund von Inaktivität automatisch ausgeloggt."
      : reason === "session_expired"
      ? "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an."
      : null;
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await login({ username, password });
      if (res?.isSuccess) {
        // Store admin status from backend
        if (res.isAdmin !== undefined) {
          localStorage.setItem("isAdmin", res.isAdmin.toString());
        }
        
        // Store user's IK for patient association
        if (res.ik) {
          localStorage.setItem("userIk", res.ik);
        }
        
        // Store hospital name
        if (res.kundeName) {
          localStorage.setItem("krankenhausName", res.kundeName);
        }
        
        // Check if 2FA PIN verification is required
        if (res.requiresPinVerification) {
          // Store username for PIN verification
          localStorage.setItem("pendingUsername", username);
          navigate("/verify-2fa");
        } else {
          // Direct login without 2FA (legacy flow)
          navigate("/patients");
        }
      } else {
        setError(res?.message ?? "Anmeldung fehlgeschlagen");
      }
    } catch (err: any) {
      setError(err?.message ?? "Fehler bei der Anmeldung");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              WHIK-Portal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bitte melden Sie sich mit Ihren Zugangsdaten an
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                id="username"
                label="Benutzername"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                fullWidth
              />

              <TextField
                id="password"
                label="Passwort"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
              />

              {inactivityMessage && <Alert severity="warning">{inactivityMessage}</Alert>}

              {successMessage && <Alert severity="success">{successMessage}</Alert>}

              {error && (
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading || !username || !password}
                sx={{ color: "white" }}
              >
                {loading ? "Anmelden..." : "Anmelden"}
              </Button>

              <Button
                variant="text"
                size="small"
                fullWidth
                onClick={() => {
                  if (!username) {
                    setError("Bitte geben Sie zuerst Ihren Benutzernamen ein.");
                    return;
                  }
                  navigate(`/change-password?username=${encodeURIComponent(username)}`);
                }}
              >
                Passwort vergessen
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
